// AiGovScheduleDialog.jsx
// Schedule dialog for AI Governance cadence tasks — creates a ticket_schedules
// row that fires recurring CW tickets. Mirrors TicketScheduleSection logic
// but styled to match the AI Gov page (plain CSS, no MUI).

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { aiGovApi, cwApi } from '../services/api';

const P  = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';

const CADENCE_TO_FREQ = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annually' };
const FREQUENCIES = ['Monthly', 'Quarterly', 'Annually', 'Weekly', 'Daily'];
const DURATION_OPTIONS = [
  { value: 0.25, label: '15 minutes' },
  { value: 0.5,  label: '30 minutes' },
  { value: 1,    label: '1 hour' },
  { value: 2,    label: '2 hours' },
  { value: 4,    label: '4 hours' },
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' }, { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' }, { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September'},{ value: 10, label: 'October' },
  { value: 11, label: 'November'},{ value: 12, label: 'December' },
];
const QUARTERLY_SETS = {
  1: [1, 4, 7, 10], 2: [2, 5, 8, 11], 3: [3, 6, 9, 12],
};
const QUARTERLY_START_OPTIONS = [
  { value: 1, label: 'Jan / Apr / Jul / Oct' },
  { value: 2, label: 'Feb / May / Aug / Nov' },
  { value: 3, label: 'Mar / Jun / Sep / Dec' },
];

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(74,43,112,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const box = {
  background: 'white', borderRadius: 12, padding: 28, width: 560,
  maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(74,43,112,0.25)',
};
const label = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: PD };
const inp = {
  width: '100%', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6,
  fontSize: 14, color: '#374151', boxSizing: 'border-box', outline: 'none',
};
const primaryBtn = {
  padding: '8px 18px', background: P, color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const outlineBtn = {
  padding: '8px 18px', background: 'transparent', color: P,
  border: `2px solid ${P}`, borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};

export default function AiGovScheduleDialog({ programId, task, clientName, onClose, onSaved }) {
  const [form, setForm] = useState({
    frequency:           CADENCE_TO_FREQ[task.cadence] || 'Quarterly',
    run_day:             15,
    run_months:          QUARTERLY_SETS[1],
    quarterly_start:     1,
    summary:             `${task.title} — ${clientName}`,
    initial_description: task.description || '',
    duration_hours:      0.5,
    board_id: '', board_name: '',
    status_id: '', status_name: '',
    type_id: '', type_name: 'Request',
    subtype_id: '', subtype_name: 'Recurring Service',
    item_id: '', item_name: 'AI Governance',
    assigned_to_id: '', assigned_to_name: '',
  });

  const [boards, setBoards]     = useState([]);
  const [boardOpts, setBoardOpts] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes]       = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [items, setItems]       = useState([]);
  const [members, setMembers]   = useState([]);
  const [cwLoading, setCwLoading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [existingSchedule, setExistingSchedule] = useState(null);
  const activeBoardId = useRef(null);

  // Load existing schedule + boards + members on mount
  useEffect(() => {
    const init = async () => {
      setCwLoading(true);
      try {
        const [schedRes, boardRes, memberRes] = await Promise.all([
          aiGovApi.getTaskSchedule(programId, task.id),
          cwApi.getBoards(),
          cwApi.getMembers().catch(() => ({ data: { members: [] } })),
        ]);
        const boards = boardRes.data.boards || [];
        setBoards(boards);
        setMembers(memberRes.data.members || []);

        const existing = schedRes.data.schedule;
        if (existing) {
          setExistingSchedule(existing);
          const qs = existing.run_months?.length > 0
            ? (Object.entries(QUARTERLY_SETS).find(([, v]) => v[0] === existing.run_months[0])?.[0] || 1)
            : 1;
          setForm(f => ({
            ...f,
            frequency:    existing.frequency || f.frequency,
            run_day:      existing.run_day ?? 15,
            run_months:   existing.run_months || QUARTERLY_SETS[1],
            quarterly_start: Number(qs),
            summary:      existing.summary || f.summary,
            initial_description: existing.initial_description || f.initial_description,
            duration_hours: existing.duration_hours || 0.5,
            board_id:     existing.board_id || '',
            board_name:   existing.board_name || '',
            status_id:    existing.status_id || '',
            status_name:  existing.status_name || '',
            type_id:      existing.type_id || '',
            type_name:    existing.type_name || 'Request',
            subtype_id:   existing.subtype_id || '',
            subtype_name: existing.subtype_name || 'Recurring Service',
            item_id:      existing.item_id || '',
            item_name:    existing.item_name || 'AI Governance',
            assigned_to_id:   existing.assigned_to_id || '',
            assigned_to_name: existing.assigned_to_name || '',
          }));
          if (existing.board_id) loadBoardOptions(existing.board_id, existing);
        } else {
          // Try to auto-select "Recurring" board
          const recurring = boards.find(b => b.name?.toLowerCase().includes('recurring'));
          if (recurring) {
            setForm(f => ({ ...f, board_id: recurring.id, board_name: recurring.name }));
            loadBoardOptions(recurring.id, null);
          }
        }
      } catch (e) {
        toast.error('Failed to load schedule data');
      } finally {
        setCwLoading(false);
      }
    };
    init();
  }, []);

  const loadBoardOptions = async (boardId, prefill) => {
    activeBoardId.current = boardId;
    try {
      const r = await cwApi.getBoardOptions(boardId);
      if (activeBoardId.current !== boardId) return;
      const opts = r.data;
      setBoardOpts(opts);
      setStatuses(opts.statuses || []);
      setTypes(opts.types || []);

      // Auto-select Type = "Request"
      const matchType = (opts.types || []).find(t => t.name === 'Request');
      if (matchType) {
        const subs = _subtypesForType(opts, matchType.id);
        setSubtypes(subs);
        const matchSub = subs.find(s => s.name === 'Recurring Service');
        if (matchSub) {
          const its = _itemsForSubtype(opts, matchSub.id);
          setItems(its);
          const matchItem = its.find(i => i.name === 'AI Governance');
          setForm(f => ({
            ...f,
            type_id:    prefill?.type_id    || matchType.id,
            type_name:  prefill?.type_name  || matchType.name,
            subtype_id:   prefill?.subtype_id   || matchSub.id,
            subtype_name: prefill?.subtype_name || matchSub.name,
            item_id:    prefill?.item_id    || (matchItem?.id ?? ''),
            item_name:  prefill?.item_name  || (matchItem?.name ?? 'AI Governance'),
            status_id:  prefill?.status_id  || (opts.statuses?.[0]?.id ?? ''),
            status_name: prefill?.status_name || (opts.statuses?.[0]?.name ?? ''),
          }));
        } else {
          setForm(f => ({
            ...f,
            type_id: prefill?.type_id || matchType.id,
            type_name: prefill?.type_name || matchType.name,
          }));
        }
      }
    } catch {
      toast.error('Failed to load board options');
    }
  };

  const _subtypesForType = (opts, typeId) => {
    const tid = Number(typeId);
    return (opts?.subtypes || []).filter(s => {
      const ids = s.typeAssociationIds || s.type_association_ids || [];
      return ids.includes(tid);
    });
  };
  const _itemsForSubtype = (opts, subtypeId) => {
    const key = String(subtypeId);
    const scoped = (opts?.itemsBySubtype || {})[key];
    return (scoped?.length > 0) ? scoped : (opts?.items || []);
  };

  const onBoardChange = (boardId, boardName) => {
    setForm(f => ({
      ...f, board_id: boardId, board_name: boardName,
      status_id: '', status_name: '',
      type_id: '', type_name: '', subtype_id: '', subtype_name: '',
      item_id: '', item_name: '',
    }));
    setBoardOpts(null);
    setStatuses([]); setTypes([]); setSubtypes([]); setItems([]);
    if (boardId) loadBoardOptions(boardId, null);
  };

  const onTypeChange = (typeId, typeName) => {
    setForm(f => ({ ...f, type_id: typeId, type_name: typeName, subtype_id: '', subtype_name: '', item_id: '', item_name: '' }));
    setSubtypes(_subtypesForType(boardOpts, typeId));
    setItems([]);
  };

  const onSubtypeChange = (subtypeId, subtypeName) => {
    setForm(f => ({ ...f, subtype_id: subtypeId, subtype_name: subtypeName, item_id: '', item_name: '' }));
    setItems(_itemsForSubtype(boardOpts, subtypeId));
  };

  const onFreqChange = (freq) => {
    let run_months = form.run_months;
    if (freq === 'Quarterly') run_months = QUARTERLY_SETS[form.quarterly_start || 1];
    else if (freq === 'Annually') run_months = [1];
    else if (freq !== 'Monthly') run_months = [];
    setForm(f => ({ ...f, frequency: freq, run_months }));
  };

  const onQuarterlyStartChange = (start) => {
    setForm(f => ({ ...f, quarterly_start: Number(start), run_months: QUARTERLY_SETS[Number(start)] }));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.frequency || !form.summary.trim()) {
      toast.error('Frequency and summary are required');
      return;
    }
    if (!form.assigned_to_id) {
      toast.error('Assignee is required');
      return;
    }
    const payload = {
      ...form,
      run_day: form.run_day !== '' ? Number(form.run_day) : null,
    };
    delete payload.quarterly_start;
    setSaving(true);
    try {
      await aiGovApi.upsertTaskSchedule(programId, task.id, payload);
      toast.success(existingSchedule ? 'Schedule updated' : 'Schedule created — tickets will be created automatically on the next due date');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this CW ticket schedule? The task will remain but no more tickets will be auto-created.')) return;
    try {
      await aiGovApi.deleteTaskSchedule(programId, task.id);
      toast.success('Schedule removed');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to remove schedule');
    }
  };

  const row = (children, cols = 1) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14, marginBottom: 14 }}>
      {children}
    </div>
  );

  const field = (lbl, children) => (
    <div>
      <label style={label}>{lbl}</label>
      {children}
    </div>
  );

  const sel = (value, onChange, opts, placeholder) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {opts.map(o => <option key={o.id ?? o.value} value={o.id ?? o.value}>{o.name ?? o.label}</option>)}
    </select>
  );

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, color: PD, fontSize: 17, fontWeight: 700 }}>Schedule CW Ticket</h3>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6B7280' }}>{task.title}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        {cwLoading && <p style={{ color: '#6B7280', fontSize: 13 }}>Loading ConnectWise data…</p>}

        <form onSubmit={handleSave}>
          {/* Summary + description */}
          {row(<>
            {field('Ticket Summary',
              <input type="text" value={form.summary} onChange={e => set('summary', e.target.value)} style={inp} required />
            )}
          </>)}
          {row(<>
            {field('Description / Instructions',
              <textarea value={form.initial_description} onChange={e => set('initial_description', e.target.value)}
                style={{ ...inp, minHeight: 60, resize: 'vertical' }} />
            )}
          </>)}

          {/* Frequency */}
          <div style={{ background: PL, borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>
            <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: PD, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Schedule</p>
            {row(<>
              {field('Frequency',
                <select value={form.frequency} onChange={e => onFreqChange(e.target.value)} style={inp}>
                  {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
              {field('Duration',
                <select value={form.duration_hours} onChange={e => set('duration_hours', parseFloat(e.target.value))} style={inp}>
                  {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              )}
            </>, 2)}

            {form.frequency === 'Weekly' && row(<>
              {field('Day of Week',
                <select value={form.run_day} onChange={e => set('run_day', Number(e.target.value))} style={inp}>
                  {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              )}
            </>)}

            {form.frequency === 'Monthly' && row(<>
              {field('Day of Month',
                <input type="number" min={1} max={28} value={form.run_day}
                  onChange={e => set('run_day', Number(e.target.value))} style={inp} />
              )}
            </>)}

            {form.frequency === 'Quarterly' && row(<>
              {field('Quarter Cycle',
                <select value={form.quarterly_start} onChange={e => onQuarterlyStartChange(e.target.value)} style={inp}>
                  {QUARTERLY_START_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              {field('Day of Month',
                <input type="number" min={1} max={28} value={form.run_day}
                  onChange={e => set('run_day', Number(e.target.value))} style={inp} />
              )}
            </>, 2)}

            {form.frequency === 'Annually' && row(<>
              {field('Month',
                <select value={form.run_months?.[0] || 1}
                  onChange={e => set('run_months', [Number(e.target.value)])} style={inp}>
                  {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              )}
              {field('Day of Month',
                <input type="number" min={1} max={28} value={form.run_day}
                  onChange={e => set('run_day', Number(e.target.value))} style={inp} />
              )}
            </>, 2)}
          </div>

          {/* Board cascade */}
          <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '14px 16px', marginBottom: 14, border: '1px solid #E5E7EB' }}>
            <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ConnectWise Ticket Details</p>
            {row(<>
              {field('Board',
                <select value={form.board_id} onChange={e => {
                  const b = boards.find(b => String(b.id) === e.target.value);
                  onBoardChange(e.target.value, b?.name || '');
                }} style={inp}>
                  <option value="">— Select board —</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              )}
              {field('Status',
                sel(form.status_id, v => {
                  const s = statuses.find(s => String(s.id) === v);
                  set('status_id', v); set('status_name', s?.name || '');
                }, statuses, '— Select status —')
              )}
            </>, 2)}
            {row(<>
              {field('Type',
                <select value={form.type_id} onChange={e => {
                  const t = types.find(t => String(t.id) === e.target.value);
                  onTypeChange(e.target.value, t?.name || '');
                }} style={inp} disabled={!types.length}>
                  <option value="">— Select type —</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              {field('Subtype',
                <select value={form.subtype_id} onChange={e => {
                  const s = subtypes.find(s => String(s.id) === e.target.value);
                  onSubtypeChange(e.target.value, s?.name || '');
                }} style={inp} disabled={!subtypes.length}>
                  <option value="">— Select subtype —</option>
                  {subtypes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </>, 2)}
            {row(<>
              {field('Item',
                <select value={form.item_id} onChange={e => {
                  const i = items.find(i => String(i.id) === e.target.value);
                  set('item_id', e.target.value); set('item_name', i?.name || '');
                }} style={inp} disabled={!items.length}>
                  <option value="">— Select item —</option>
                  {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              )}
              {field('Assign To',
                <select value={form.assigned_to_id} onChange={e => {
                  const m = members.find(m => String(m.id) === e.target.value);
                  set('assigned_to_id', e.target.value); set('assigned_to_name', m?.identifier || m?.name || '');
                }} style={inp}>
                  <option value="">— Select engineer —</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name || m.identifier}</option>)}
                </select>
              )}
            </>, 2)}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            {existingSchedule ? (
              <button type="button" onClick={handleRemove}
                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Remove Schedule
              </button>
            ) : <span />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ ...outlineBtn, fontSize: 13, padding: '6px 16px' }}>Cancel</button>
              <button type="submit" style={{ ...primaryBtn, fontSize: 13, padding: '6px 16px' }} disabled={saving || cwLoading}>
                {saving ? 'Saving…' : existingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
